export type StockState = 'in_stock' | 'in_build' | 'built' | 'in_test' | 'allocated' | 'shipped' | 'deployed' | 'retired' | 'other';
export type IntakeParser = 'soten_qr' | 'raw_serial' | 'ean';
export type ProductCategory = 'tablet' | 'gps_module' | 'sensor' | 'accessory' | 'other';

export interface AttributeSchemaField {
  key: string;
  label: string;
  required: boolean;
}

export interface StockProduct {
  id: string;
  sku: string;
  name: string;
  manufacturer: string | null;
  category: ProductCategory;
  hasBuildStep: boolean;
  intakeParser: IntakeParser;
  attributesSchema: AttributeSchemaField[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StockItem {
  id: string;
  productId: string;
  product?: Pick<StockProduct, 'id' | 'sku' | 'name' | 'manufacturer' | 'category' | 'hasBuildStep' | 'intakeParser' | 'attributesSchema'>;
  serialNumber: string;
  state: StockState;
  batchTag: string | null;
  wifiMacAddress: string | null;
  attributes: Record<string, string>;
  receivedAt: string;
  receivedBy: string | null;
  receivedByName?: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  latestBuild?: StockBuild | null;
  latestAllocation?: StockAllocation | null;
}

export interface StockItemDetail extends StockItem {
  stateHistory: StockStateHistoryEntry[];
  builds: StockBuild[];
}

export interface StockStateHistoryEntry {
  id: string;
  stockItemId: string;
  fromState: string | null;
  toState: string;
  actor: string | null;
  actorName?: string | null;
  notes: string | null;
  createdAt: string;
}

export interface StockBuild {
  id: string;
  stockItemId: string;
  builtAt: string;
  builtBy: string | null;
  appVersions: Record<string, string>;
  gitSha: string | null;
  macAddress: string | null;
  androidVersion: string | null;
  notes: string | null;
  createdAt: string;
}

export interface StockAllocation {
  id: string;
  stockItemId: string;
  orderId: string | null;
  allocatedBy: string | null;
  allocatedAt: string;
  shippedAt: string | null;
  trackingRef: string | null;
  carrier: string | null;
  notes: string | null;
}

export interface InventoryStats {
  total: number;
  byState: Partial<Record<StockState, number>>;
  byProduct: { productId: string; sku: string; name: string; count: number }[];
}

export interface ReceiveBatchResult {
  received: number;
  failed: number;
  results: { serial: string; ok: boolean; error?: string; item?: StockItem }[];
}

export const STATE_LABELS: Record<StockState, string> = {
  in_stock: 'In Stock',
  in_build: 'In Build',
  built: 'Built',
  in_test: 'In Test',
  allocated: 'Allocated',
  shipped: 'Shipped',
  deployed: 'Deployed',
  retired: 'Retired',
  other: 'Other',
};

export const STATE_COLORS: Record<StockState, string> = {
  in_stock: 'bg-slate-500/20 text-slate-300',
  in_build: 'bg-yellow-500/20 text-yellow-400',
  built: 'bg-cyan-500/20 text-cyan-400',
  in_test: 'bg-orange-500/20 text-orange-400',
  allocated: 'bg-purple-500/20 text-purple-400',
  shipped: 'bg-blue-500/20 text-blue-400',
  deployed: 'bg-green-500/20 text-green-400',
  retired: 'bg-red-500/20 text-red-400',
  other: 'bg-zinc-500/20 text-zinc-300',
};

export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  tablet: 'Tablet',
  gps_module: 'GPS Module',
  sensor: 'Sensor',
  accessory: 'Accessory',
  other: 'Other',
};

// The lifecycle state machine — which states an item may move to next. Mirrors
// VALID_TRANSITIONS on the API (inventoryService.ts); the backend enforces it,
// the UI uses it to only offer legal transitions.
export const ALL_STATES: StockState[] = ['in_stock', 'in_build', 'built', 'in_test', 'allocated', 'shipped', 'deployed', 'retired', 'other'];

export const VALID_NEXT_STATES: Record<StockState, StockState[]> = {
  in_stock: ['in_build', 'retired', 'other'],
  in_build: ['built', 'in_stock', 'other'],
  built: ['in_test', 'allocated', 'in_stock', 'other'],
  in_test: ['built', 'allocated', 'other'],
  allocated: ['shipped', 'built', 'other'],
  shipped: ['deployed', 'allocated', 'other'],
  deployed: ['retired', 'other'],
  retired: [],
  other: ['in_stock', 'retired'],
};
